import styled from "styled-components";
import { ReactComponent as ExternalLinkIcon } from "./../assets/images/external_website.svg";
import { mobileMaxWidth, colors, fonts, radii, transitions } from "../theme";

const Card = styled.article<{ mirrorImage: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radii.xl};
  box-shadow: ${colors.shadowCard};
  overflow: hidden;
  transition: ${transitions.default};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${colors.shadowHeavy};
  }

  > :first-child {
    order: ${({ mirrorImage }) => (mirrorImage ? 1 : 0)};
  }

  @media screen and (max-width: ${mobileMaxWidth}) {
    grid-template-columns: 1fr;

    > :first-child {
      order: 0;
    }
  }
`;

const Visual = styled.div<{ tint: string }>`
  position: relative;
  min-height: 440px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 48px 24px 0;
  overflow: hidden;
  background: radial-gradient(
      circle at 50% 100%,
      ${({ tint }) => tint}55 0%,
      transparent 70%
    ),
    ${({ tint }) => tint}14;

  @media screen and (max-width: ${mobileMaxWidth}) {
    min-height: 320px;
    padding-top: 32px;
  }
`;

const AppImage = styled.img`
  width: 240px;
  max-width: 70%;
  height: auto;
  margin-bottom: -90px;
  filter: drop-shadow(0 24px 40px rgba(11, 18, 32, 0.25));
  transition: ${transitions.default};

  ${Card}:hover & {
    transform: translateY(-8px);
  }

  @media screen and (max-width: ${mobileMaxWidth}) {
    width: 190px;
    margin-bottom: -70px;
  }
`;

const Content = styled.div`
  padding: 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media screen and (max-width: ${mobileMaxWidth}) {
    padding: 32px 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AppIcon = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  box-shadow: ${colors.shadowMedium};
`;

const ProjectTitle = styled.h3`
  margin: 0;
  font-family: ${fonts.heading};
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${colors.ink};

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    transition: ${transitions.fast};

    &:hover {
      color: ${colors.accent};
    }
  }
`;

const ExternalLinkIconStyled = styled(ExternalLinkIcon)`
  width: 18px;
  height: 18px;
  fill: currentColor;
  opacity: 0.5;

  path {
    fill: currentColor;
  }

  path:first-child {
    fill: none;
  }
`;

const ProjectBody = styled.p`
  margin: 20px 0 0;
  color: ${colors.textSecondary};
  font-family: ${fonts.body};
  font-size: 16px;
  line-height: 1.7;
`;

const Features = styled.ul`
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${colors.ink};
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 500;
`;

const Check = styled.span<{ tint: string }>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ tint }) => tint}22;
  color: ${({ tint }) => tint};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Buttons = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const StoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: ${radii.medium};
  background: ${colors.ink};
  color: ${colors.white};
  text-decoration: none;
  font-family: ${fonts.body};
  transition: ${transitions.default};

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }

  span {
    display: flex;
    flex-direction: column;
    line-height: 1.15;
  }

  small {
    font-size: 10px;
    opacity: 0.7;
  }

  strong {
    font-size: 15px;
    font-weight: 600;
  }

  &[href]:hover {
    transform: translateY(-2px);
    box-shadow: ${colors.shadowMedium};
  }
`;

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 6.2 5 8.5l4.5-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.77-3.32-1.8-1.41-.14-2.76.83-3.47.83-.72 0-1.82-.81-2.99-.79-1.54.02-2.96.9-3.75 2.27-1.6 2.78-.41 6.89 1.15 9.14.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.78.74 2.99.72 1.24-.02 2.02-1.12 2.77-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.66zM14.1 5.86c.63-.77 1.06-1.83.94-2.89-.91.04-2.02.61-2.67 1.37-.58.67-1.1 1.76-.96 2.8 1.02.08 2.06-.52 2.69-1.28z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3.6 2.3c-.2.2-.3.6-.3 1v17.4c0 .4.1.8.3 1l9.5-9.7L3.6 2.3zm10.6 10.8 2.7 2.8-11.3 6.4c-.4.2-.8.3-1.1.2l9.7-9.4zm0-2.2L4.5 1.5c.3-.1.7 0 1.1.2l11.3 6.4-2.7 2.8zm3.9-.6 3 1.7c.9.5.9 1.4 0 1.9l-3 1.7-2.9-2.6 2.9-2.7z" />
  </svg>
);

type props = {
  appImage: string;
  mirrorImage: boolean;
  appIcon: string;
  title: string;
  body: string;
  checkboxTexts: string[];
  appStoreLink?: string;
  googlePlayLink?: string;
  externalWebsite?: string;
  tint: string;
};

const ProjectItem = ({
  mirrorImage,
  appImage,
  appIcon,
  title,
  body,
  checkboxTexts,
  appStoreLink,
  googlePlayLink,
  externalWebsite,
  tint,
}: props) => {
  return (
    <Card mirrorImage={mirrorImage}>
      <Visual tint={tint}>
        <AppImage src={appImage} alt={`Skärmbild från appen ${title}`} />
      </Visual>
      <Content>
        <Header>
          <AppIcon src={appIcon} alt="" />
          <ProjectTitle>
            {externalWebsite !== undefined ? (
              <a href={externalWebsite} target="_blank" rel="noreferrer">
                {title}
                <ExternalLinkIconStyled aria-hidden="true" />
              </a>
            ) : (
              title
            )}
          </ProjectTitle>
        </Header>
        <ProjectBody>{body}</ProjectBody>

        <Features>
          {checkboxTexts.map((value, key) => (
            <Feature key={key}>
              <Check tint={tint}>
                <CheckIcon />
              </Check>
              {value}
            </Feature>
          ))}
        </Features>
        <Buttons>
          <StoreButton
            href={appStoreLink}
            target={appStoreLink && "_blank"}
            rel={appStoreLink && "noreferrer"}
          >
            <AppleIcon />
            <span>
              <small>Ladda ned i</small>
              <strong>App Store</strong>
            </span>
          </StoreButton>
          <StoreButton
            href={googlePlayLink}
            target={googlePlayLink && "_blank"}
            rel={googlePlayLink && "noreferrer"}
          >
            <PlayIcon />
            <span>
              <small>Hämta på</small>
              <strong>Google Play</strong>
            </span>
          </StoreButton>
        </Buttons>
      </Content>
    </Card>
  );
};

export default ProjectItem;
